'use client';

import { type KeyboardEvent, useCallback, useRef, useState } from 'react';

import { CommunityChannelMessageTextContentType } from '@ps/models/lib/community-channel';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { cn } from '@ps/ui/utils';
import { SendHorizontal, SmilePlus } from 'lucide-react';
import { type Descendant, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import type {
  ChannelMessageLocalAttachment,
  SendChannelMessageParams,
} from '../../types/ExtendedCommunityChannelMessage';
import { ChannelComposerMentionPicker } from '../channel-message-editor/ChannelComposerMentionPicker';
import { ChannelComposerToolbar } from '../channel-message-editor/ChannelComposerToolbar';
import { SlateEditorElement } from '../channel-message-editor/elements/SlateEditorElement';
import { SlateEditorLeaf } from '../channel-message-editor/leaves/SlateEditorLeaf';
import { EmojiPickerPopover } from '../common/EmojiPickerPopover';
import { ChannelComposerAttachMenu } from './attachments/ChannelComposerAttachMenu';
import { ChannelComposerAttachmentStrip } from './attachments/ChannelComposerAttachmentStrip';
import {
  type ComposerAttachmentRejection,
  useComposerAttachments,
} from './attachments/useComposerAttachments';
import { ChannelComposerLinkStrip } from './links/ChannelComposerLinkStrip';
import { useComposerLinks } from './links/useComposerLinks';
import { withLinks } from './plugins/withLinks';
import { withMarkdownShortcuts } from './plugins/withMarkdownShortcuts';
import { withMentions } from './plugins/withMentions';
import { PostPickerDialog } from './post-picker/PostPickerDialog';
import type { CustomEditor } from './types/SlateNodes';
import { slateToPlainText } from './utils/slateToPlainText';

interface Props {
  /**
   * Receives the serialized Slate document on every successful send.
   * The composer always produces `slatejs-dom` content type — plain
   * text and Markdown content types are reserved for legacy inputs
   * that aren't authored through the rich editor.
   */
  onSend: (payload: SendChannelMessageParams) => Promise<void>;
  /**
   * Channel id is forwarded to the mention picker so it can search
   * the right channel's member list. When absent (rare — typically
   * during route transitions) the picker stays inactive.
   */
  channelId?: string | null;
  disabled?: boolean;
  /** Prompt text; defaults to the main-channel composer copy. */
  placeholder?: string;
  /**
   * Extra bottom space, in px, for the mounts where this composer is the
   * bottom-most element on screen and the fixed mobile tab bar would cover it
   * (the channel detail pane). Measured by the caller. Left `undefined` by the
   * inline-reply mount, which is nested inside the message list and never
   * touches the viewport bottom.
   */
  bottomInset?: number;
}

// Cast through `unknown` because Slate's library type for
// `Descendant` is augmented to a different node union by another
// workspace slice (TemplateEditor). Locally we always produce
// nodes that match our own `ParagraphElement` shape — the cast
// just satisfies the cross-slice typecheck.
const kInitialValue = [
  { type: 'paragraph', children: [{ text: '' }] },
] as unknown as Descendant[];

/**
 * Rich-text composer powered by Slate.js. Wraps the editor with the
 * usual `withHistory + withReact` plus three slice-local plugins:
 *
 *   - `withMarkdownShortcuts` — `*` / `-` / `+` / `1.` / `>` + space
 *     promotes to list / block-quote.
 *   - `withLinks` — http(s) URLs auto-promote to clickable `link`
 *     elements as the user types or pastes.
 *   - `withMentions` — registers `mention` chips as inline + void so
 *     the cursor treats them as atoms. The popover lives in
 *     `ChannelComposerMentionPicker`.
 *
 * On send the editor's `children` are JSON-stringified and posted
 * with `contentType: 'slatejs-dom'`. The receiver-side renderer
 * (`SlateMessageRenderer`) already handles the same node set.
 *
 * Enter submits, Shift+Enter inserts a soft break.
 */
export function ChannelComposer({
  onSend,
  channelId,
  disabled,
  placeholder = 'Enjoy the community by start chatting with others!',
  bottomInset,
}: Props) {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  // The composer in the dashboard surface is the creator viewing
  // their own channel, so the mention-picker's creatorId is the
  // signed-in user. Falls back to context if available.
  const mentionCreatorId = creatorId ?? currentUserId ?? undefined;

  const [editor] = useState<CustomEditor>(() =>
    withMentions(
      withMarkdownShortcuts(withLinks(withReact(withHistory(createEditor())))),
    ),
  );

  const pickerRef = useRef<{
    handleChange: (editor: CustomEditor, value: Descendant[]) => boolean;
    handleKeyDown: (
      editor: CustomEditor,
      event: KeyboardEvent<HTMLDivElement>,
    ) => boolean;
  }>(null);

  const [hasContent, setHasContent] = useState(false);
  // Tracked manually instead of via `useTransition` — React 19's
  // async transitions defer state updates inside the `await` until
  // the entire promise resolves, which suppresses the optimistic
  // bubble's spinner render until after the send completes. Plain
  // `useState(false)` keeps the optimistic update at urgent
  // priority so the bubble renders the moment the provider inserts
  // it.
  const [pending, setPending] = useState(false);
  const [postPickerOpen, setPostPickerOpen] = useState(false);
  const attach = useComposerAttachments();
  const linkState = useComposerLinks();

  const reset = useCallback(() => {
    editor.children = kInitialValue;
    editor.select({
      anchor: { path: [0, 0], offset: 0 },
      focus: { path: [0, 0], offset: 0 },
    });
    editor.onChange();
    setHasContent(false);
  }, [editor]);

  const submit = useCallback(() => {
    if (pending || disabled) return;
    if (!hasContent && !attach.hasAttachments && !linkState.hasLinks) return;
    const snapshot = editor.children;
    // Snapshot the local attachments BEFORE clearing the composer.
    // The optimistic-send path keeps these object URLs alive until
    // the upload finishes (or fails); revoking them in `attach.clear`
    // would invalidate the bubble's local preview tiles.
    const localAttachments: ChannelMessageLocalAttachment[] =
      attach.attachments.map(item => ({
        id: item.id,
        filename: item.filename,
        contentType: item.contentType,
        localFileUri: item.localPreviewUrl,
        localFile: item.file,
      }));
    const linksSnapshot = linkState.payload;
    reset();
    // Detach without revoking — the bubble's local preview keeps the
    // same object URLs alive. The provider's `removeMessage` /
    // Firestore reconciliation revokes them when the optimistic row
    // exits.
    attach.detach();
    linkState.clear();
    setPending(true);
    // Run as a plain async — see the `useState` comment above for
    // why we don't wrap in `startTransition`.
    void (async () => {
      try {
        await onSend({
          content: JSON.stringify(snapshot),
          contentType: CommunityChannelMessageTextContentType.SlateJsDom,
          attachments: localAttachments.length ? localAttachments : undefined,
          links: linksSnapshot.length ? linksSnapshot : undefined,
        });
      } catch (error) {
        // The failed send already lives in the channel as an
        // optimistic bubble with `sendingError` set — the user
        // retries from the bubble. The composer stays cleared so
        // we don't double-track the same content.
        console.error('community channel send failed', error);
      } finally {
        setPending(false);
      }
    })();
  }, [editor, hasContent, pending, disabled, onSend, reset, attach, linkState]);

  const handleFilesSelected = (files: FileList) => {
    const rejections = attach.add(files);
    if (rejections.length) {
      logRejections(rejections);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (pickerRef.current?.handleKeyDown(editor, event)) {
      return;
    }
    if (event.key === 'Enter') {
      if (event.shiftKey) return;
      event.preventDefault();
      submit();
    }
  };

  const onEmojiSelect = (reaction: { emoji: string }) => {
    editor.insertText(reaction.emoji);
    // Refocus the editor after picker close — `ReactEditor.focus` is
    // the Slate equivalent of `inputRef.current?.focus()`.
    try {
      ReactEditor.focus(editor);
    } catch {
      // The editor may be unmounted briefly during a fast send +
      // reset. Ignore the throw — the picker will retry on next
      // open.
    }
  };

  const canSend =
    (hasContent || attach.hasAttachments || linkState.hasLinks) &&
    !pending &&
    !disabled;

  return (
    <form
      className="bg-surface-canvas border-line-strong/80 [[data-theme=visitor]_&]:border-line min-w-0 shrink-0 overflow-hidden border-t px-4 py-3"
      // Padding (not margin) so the composer's own `bg-scrim/40` fills the
      // reserved strip and the tab bar overlays it, rather than leaving a gap
      // showing the page behind.
      style={bottomInset == null ? undefined : { paddingBottom: bottomInset }}
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="bg-surface-1 min-w-0 overflow-hidden rounded-2xl px-3 py-2">
        <Slate
          editor={editor}
          initialValue={kInitialValue}
          onChange={value => {
            const text = slateToPlainText(value);
            setHasContent(text.trim().length > 0);
            pickerRef.current?.handleChange(
              editor,
              value as unknown as Descendant[],
            );
          }}
        >
          <ChannelComposerToolbar />
          <Editable
            renderElement={SlateEditorElement}
            renderLeaf={SlateEditorLeaf}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            className="text-content-1 [&_[data-slate-placeholder=true]]:text-content-placeholder [[data-theme=visitor]_&]:[&_[data-slate-placeholder=true]]:text-content-4 max-h-40 overflow-y-auto py-1 text-sm outline-none"
          />
          <ChannelComposerAttachmentStrip
            attachments={attach.attachments}
            onRemove={attach.remove}
          />
          <ChannelComposerLinkStrip
            links={linkState.links}
            onRemove={linkState.remove}
          />
          <div className="mt-1 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ChannelComposerAttachMenu
                onFilesSelected={handleFilesSelected}
                onAttachPost={
                  creatorId ? () => setPostPickerOpen(true) : undefined
                }
                disabled={disabled}
              />
              <EmojiPickerPopover
                trigger={
                  <button
                    type="button"
                    aria-label="Add emoji"
                    className="hover:text-content-1 text-content-3"
                  >
                    <SmilePlus className="h-5 w-5" aria-hidden />
                  </button>
                }
                onSelect={onEmojiSelect}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-content-4 hidden text-[11px] sm:inline">
                Shift + Enter to add a new line
              </span>
              <button
                type="submit"
                disabled={!canSend}
                aria-label="Send"
                className={cn(
                  'rounded-full p-1.5 transition',
                  canSend
                    ? 'text-brand-text hover:bg-brand/10'
                    : 'text-content-4 cursor-not-allowed',
                )}
              >
                <SendHorizontal className="h-5 w-5" aria-hidden />
              </button>
            </div>
          </div>
          <ChannelComposerMentionPicker
            ref={pickerRef}
            creatorId={mentionCreatorId}
            channelId={channelId ?? null}
          />
        </Slate>
      </div>
      <PostPickerDialog
        open={postPickerOpen}
        onOpenChange={setPostPickerOpen}
        storeId={creatorId}
        onSelect={post => {
          linkState.addPost(post);
        }}
      />
    </form>
  );
}

function logRejections(rejections: ComposerAttachmentRejection[]): void {
  // No toast infra wired into this slice yet — fall back to a
  // best-effort `console.warn` so engineers see what was dropped.
  // The next polish pass can replace this with a real toast.
  for (const reason of rejections) {
    if (reason.kind === 'unsupported') {
      console.warn(`Unsupported file type: ${reason.filename}`);
    } else {
      const mb = Math.round(reason.limitBytes / (1024 * 1024));
      console.warn(
        `${reason.filename} exceeds the ${mb} MB limit and was skipped`,
      );
    }
  }
}
